import type { ApolloClient } from "@apollo/client";
import { IMAGE_MEDIA_TYPES, VIDEO_MEDIA_TYPES } from "@package/ui-config";
import {
  type Device,
  GetDeviceDocument,
  type GetDeviceQuery,
  type GetDeviceQueryVariables,
  GetLayoutDocument,
  type GetLayoutQuery,
  type GetLayoutQueryVariables,
  GetLocationDocument,
  type GetLocationQuery,
  type GetLocationQueryVariables,
  GetMediaDocument,
  GetMediaDownloadUrlDocument,
  type GetMediaDownloadUrlQuery,
  type GetMediaDownloadUrlQueryVariables,
  type GetMediaQuery,
  type GetMediaQueryVariables,
  GetPostDocument,
  GetPostListFullDocument,
  type GetPostListFullQuery,
  type GetPostListFullQueryVariables,
  type GetPostQuery,
  type GetPostQueryVariables,
  GetProfileDocument,
  type GetProfileQuery,
  type GetProfileQueryVariables,
  GetServiceListFullDocument,
  type GetServiceListFullQuery,
  type GetServiceListFullQueryVariables,
  type Layout,
  type Location,
  type Media,
  type Post,
  type ProfileModel,
  type Service,
} from "@package/ui-gql/index-data-access";
import { type SliceWallMediaType } from "../redux/slice-wall";

export async function getDevice(
  client: ApolloClient<object>,
  id: string,
): Promise<Device | undefined> {
  try {
    const device = await client.query<GetDeviceQuery, GetDeviceQueryVariables>({
      query: GetDeviceDocument,
      variables: { id },
    });

    return device?.data?.item;
  } catch (e) {
    console.error("[wall-utils-gql]: getDevice", e);
  }

  return undefined;
}

export async function getLocation(
  client: ApolloClient<object>,
  id: string,
): Promise<Location | undefined> {
  try {
    const location = await client.query<
      GetLocationQuery,
      GetLocationQueryVariables
    >({
      query: GetLocationDocument,
      variables: { id },
    });

    return location?.data?.item;
  } catch (e) {
    console.error("[wall-utils-gql]: getLocation", e);
  }

  return undefined;
}

export async function getProfile(
  client: ApolloClient<object>,
  id: string,
): Promise<ProfileModel | undefined> {
  try {
    const profile = await client.query<
      GetProfileQuery,
      GetProfileQueryVariables
    >({
      query: GetProfileDocument,
      variables: { id },
    });

    return profile?.data?.item;
  } catch (e) {
    console.error("[wall-utils-gql]: getProfile", e);
  }

  return undefined;
}

export async function getLayout(
  client: ApolloClient<object>,
  id: string,
): Promise<Layout | undefined> {
  try {
    const layout = await client.query<GetLayoutQuery, GetLayoutQueryVariables>({
      query: GetLayoutDocument,
      variables: { id },
    });

    return layout?.data?.item;
  } catch (e) {
    console.error("[wall-utils-gql]: getLayout", e);
  }

  return undefined;
}

export async function getLocationActiveProfile(
  client: ApolloClient<object>,
  id: string,
): Promise<ProfileModel | undefined> {
  const location = await getLocation(client, id);
  if (location && location.profile) {
    return await getProfile(client, location.profile);
  }

  return undefined;
}

export async function getMedia(
  client: ApolloClient<object>,
  id: string,
): Promise<Media | undefined> {
  try {
    const media = await client.query<GetMediaQuery, GetMediaQueryVariables>({
      query: GetMediaDocument,
      variables: { id },
    });

    if (media?.data?.item) {
      return media.data.item;
    }

    return undefined;
  } catch (e) {
    console.error("[wall-utils-gql]: getMedia", e);
  }

  return undefined;
}

export async function getMediaDownloadUrl(
  client: ApolloClient<object>,
  id: string,
): Promise<{ url: string; type: SliceWallMediaType } | undefined> {
  try {
    const media = await client.query<GetMediaQuery, GetMediaQueryVariables>({
      query: GetMediaDocument,
      variables: { id },
    });

    if (media?.data?.item?.file) {
      const mediaUrl = await client.query<
        GetMediaDownloadUrlQuery,
        GetMediaDownloadUrlQueryVariables
      >({
        query: GetMediaDownloadUrlDocument,
        variables: { fileName: media.data.item.file },
      });

      if (mediaUrl?.data?.item?.url) {
        return {
          type: getMediaType(media.data.item),
          url: mediaUrl.data.item.url,
        };
      }
    }
  } catch (e) {
    console.error("[wall-utils-gql]: getMediaDownloadUrl", e);
  }

  return undefined;
}

export function getMediaType(media: Media): SliceWallMediaType {
  if (IMAGE_MEDIA_TYPES.some((t) => !!media.type?.includes(t))) {
    return "image";
  }

  if (VIDEO_MEDIA_TYPES.some((t) => !!media.type?.includes(t))) {
    return "video";
  }

  return "binary";
}

export async function getPostsWithTagsAndLayout(
  client: ApolloClient<object>,
  tags: string[],
  layout: string,
): Promise<Post[]> {
  if (tags.length === 0) {
    return [];
  }

  try {
    const posts = await client.query<
      GetPostListFullQuery,
      GetPostListFullQueryVariables
    >({
      query: GetPostListFullDocument,
      variables: {
        filter: {
          enabled: true,
          layouts: [layout],
          tags,
        },
      },
    });

    return posts?.data?.items?.filter((p) => !!p.id) ?? [];
  } catch (e) {
    console.error("[wall-utils-gql]: getPostsWithTagsAndLayout", e);
  }

  return [];
}

export async function getPost(
  client: ApolloClient<object>,
  id: string,
): Promise<Post | undefined> {
  try {
    const post = await client.query<GetPostQuery, GetPostQueryVariables>({
      query: GetPostDocument,
      variables: {
        id,
      },
    });

    return post?.data?.item ?? undefined;
  } catch (e) {
    console.error("[wall-utils-gql]: getPost", e);
  }

  return undefined;
}

export async function getPosts(
  client: ApolloClient<object>,
  ids: string[],
): Promise<Post[]> {
  if (ids.length === 0) {
    return [];
  }

  try {
    const posts = await client.query<
      GetPostListFullQuery,
      GetPostListFullQueryVariables
    >({
      query: GetPostListFullDocument,
      variables: {
        filter: {
          ids,
        },
      },
    });

    return posts?.data?.items?.filter((p) => !!p.id) ?? [];
  } catch (e) {
    console.error("[wall-utils-gql]: getPosts", e);
  }

  return [];
}

export async function getServices(
  client: ApolloClient<object>,
): Promise<Service[]> {
  try {
    const services = await client.query<
      GetServiceListFullQuery,
      GetServiceListFullQueryVariables
    >({
      query: GetServiceListFullDocument,
    });

    return (services?.data?.items as Service[]) ?? [];
  } catch (e) {
    console.error("[wall-utils-gql]: getServices", e);
  }

  return [];
}
